/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { SelectOption } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { format, parseISO, startOfDay } from 'date-fns';
import { combineLatest, debounceTime, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { NextTaskType, TaskTypeTypology } from '../../../task-type/task-type.types';
import { NextServicio } from '../../../../modules/admin/services/services.types';
import { UserZombies, Task, WorkDay, TaskWorkDayTypeMode, FlushBeautifyTask } from '../../../../modules/admin/tasks/tasks.types';
import { DialogsService } from '../../../../dialogs/dialogs.service';
import { MasterDataService } from '../../../master-data/master-data.service';
import { ServicesService } from '../../../../modules/admin/services/services.service';
import { TasksService } from '../../../../modules/admin/tasks/tasks.service';
import { TaskTypeService } from '../../../task-type/task-type.service';

@Component({
    selector: 'beautify-task-sidebar',
    templateUrl: './beautify-task-sidebar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BeautifyTaskSidebarComponent implements OnInit, OnDestroy {

    @Output() readonly discardBeautify: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly closeBeautify: EventEmitter<void> = new EventEmitter<void>();

    task: Task;
    taskType: NextTaskType;
    service: NextServicio;

    workdayTypeOptions: SelectOption[];
    taskTypeOptions: SelectOption[];

    updatingTask: boolean = false;

    disableButton = false;
    flashMessage: 'success' | 'error' | null = null;

    // Beautify form variables
    taskForm = new FormGroup({
        address: new FormControl<any>(''),
        endDate: new FormControl<Date>(undefined, Validators.required),
        endTime: new FormControl<string>('', Validators.required),
        endTime2: new FormControl<string>('', Validators.required),
        externalTaskId: new FormControl<string>(''),
        scheduled_from_date: new FormControl<Date>(undefined),
        scheduled_from_time: new FormControl<string>(undefined),
        scheduled_from_time2: new FormControl<string>(undefined),
        scheduled_to_date: new FormControl<Date>(undefined),
        scheduled_to_time: new FormControl<string>(undefined),
        scheduled_to_time2: new FormControl<string>(undefined),
        taskTypeId: new FormControl<string>(undefined),
        workDayTypeOid: new FormControl<string>(undefined),
        startDate: new FormControl<Date>(undefined, Validators.required),
        startTime: new FormControl<string>('', Validators.required),
        startTime2: new FormControl<string>('', Validators.required),
        taskId: new FormControl<string>('', Validators.required),
        zombie: new FormControl<number>(undefined, Validators.required)
    });

    addressOptions: any[] = [];
    userZombiesCalendar: UserZombies = {
        zombies: []
    };

    private unsubscribeAll = new Subject<void>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private dialogsService: DialogsService,
        private masterDataService: MasterDataService,
        private servicesService: ServicesService,
        private tasksService: TasksService,
        private taskTypeService: TaskTypeService,
        private transloco: TranslocoService
    ) { }

    ngOnInit() {
        this.taskForm.get('address').valueChanges
            .pipe(
                takeUntil(this.unsubscribeAll),
                debounceTime(200)
            ).subscribe(async (address) => {
                address = address ?? '';
                if (this.task.addressUri && typeof address === 'string') {
                    const searchReply = await firstValueFrom(this.tasksService.searchAddress(address, this.task.addressUri));
                    this.addressOptions = searchReply?.data ?? [];
                    const findAddress = this.addressOptions.find(a => a._oid === address);
                    if (findAddress) {
                        this.taskForm.get('address').setValue(findAddress, { emitEvent: false });
                    }
                }
            });

        this.taskForm.get('workDayTypeOid').valueChanges
            .pipe(
                takeUntil(this.unsubscribeAll),
                debounceTime(200)
            ).subscribe(async (workDayTypeOid) => {
                const exportData: WorkDay = this.workdayTypeOptions.find(opt => opt.code === workDayTypeOid)?.export;
                this.task.workDayTypeMode = exportData?.mode as TaskWorkDayTypeMode;
                if (exportData?.from) {
                    this.taskForm.get('scheduled_from_time').setValue(exportData.from);
                }
                if (exportData?.to) {
                    this.taskForm.get('scheduled_to_time').setValue(exportData.to);
                }
                if (exportData?.from2) {
                    this.taskForm.get('startTime2').enable();
                    this.taskForm.get('scheduled_from_time2').enable();
                    this.taskForm.get('scheduled_from_time2').setValue(exportData.from2);
                } else {
                    this.taskForm.get('startTime2').disable();
                    this.taskForm.get('scheduled_from_time2').disable();
                }
                if (exportData?.to2) {
                    this.taskForm.get('endTime2').enable();
                    this.taskForm.get('scheduled_to_time2').enable();
                    this.taskForm.get('scheduled_to_time2').setValue(exportData.to2);
                } else {
                    this.taskForm.get('endTime2').disable();
                    this.taskForm.get('scheduled_to_time2').disable();
                }
            });

        this.tasksService.taskById$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async (task) => {
                this.task = task.task;
                this.taskType = await firstValueFrom(this.taskTypeService.retrieveTaskTypeById(this.task.taskTypeId));
                this.taskTypeOptions = await firstValueFrom(this.taskTypeService.retrieveCompatibleTaskType(this.task.serviceId, this.task.taskTypeUri));
                if (this.taskTypeOptions?.length > 0) {
                    this.taskForm.get('taskTypeId').setValue(this.taskTypeOptions.filter(item => item.isDefault)[0].code);
                }
                this.workdayTypeOptions = this.taskType.workDayTypeUri ?
                    await firstValueFrom(this.masterDataService.retrieveMasterDataAsSelectOptions(this.taskType.workDayTypeUri)) : [];
                if (this.workdayTypeOptions.length === 0) {
                    this.taskForm.get('workDayTypeOid').disable();
                }
                this.taskForm.get('workDayTypeOid').setValue(this.task.workDayTypeOid, { emitEvent: false });
                this.taskForm.get('address').setValue(this.task.addressOid);
                this.taskForm.get('taskId').setValue(this.task.taskId);
                this.taskForm.get('externalTaskId').setValue(this.task.externalTaskId);
                this.taskForm.get('zombie').setValue(this.task.zombieNumber);
                if (this.task.scheduled_from_date) {
                    this.taskForm.get('scheduled_from_date').setValue(startOfDay(new Date(this.task.scheduled_from_date)));
                    this.taskForm.get('scheduled_from_date').addValidators(Validators.required);
                    this.taskForm.get('scheduled_from_date').updateValueAndValidity();
                }
                this.taskForm.get('startDate').setValue(this.task.startTaskDate || this.task.startTaskDate2
                    ? startOfDay(new Date(this.task.startTaskDate ?? this.task.startTaskDate2)) : undefined);
                this.taskForm.get('endDate').setValue(this.task.endTaskDate || this.task.endTaskDate2
                    ? startOfDay(new Date(this.task.endTaskDate ?? this.task.endTaskDate2)) : undefined);
                this.taskForm.get('startTime').setValue(this.task.startTaskDate ? format(new Date(this.task.startTaskDate), 'HH:mm:ss') : undefined);
                this.taskForm.get('endTime').setValue(this.task.endTaskDate ? format(new Date(this.task.endTaskDate), 'HH:mm:ss') : undefined);
                this.taskForm.get('scheduled_from_time').setValue(this.task.scheduled_from_time ? format(new Date(this.task.scheduled_from_time), 'HH:mm:ss') : undefined);
                this.taskForm.get('scheduled_to_time').setValue(this.task.scheduled_to_time ? format(new Date(this.task.scheduled_to_time), 'HH:mm:ss') : undefined);
                if (this.task.taskTypeTypology === TaskTypeTypology.withTime) {
                    this.taskForm.get('scheduled_to_date').disable();
                    if (this.task.workDayTypeMode === TaskWorkDayTypeMode.split) {
                        this.taskForm.get('endTime2').setValue(this.task.endTaskDate2 ? format(new Date(this.task.endTaskDate2), 'HH:mm:ss') : undefined);
                        this.taskForm.get('startTime2').setValue(this.task.startTaskDate2 ? format(new Date(this.task.startTaskDate2), 'HH:mm:ss') : undefined);
                        this.taskForm.get('scheduled_from_time2').setValue(this.task.scheduled_from_time2
                            ? format(new Date(this.task.scheduled_from_time2), 'HH:mm:ss') : undefined);
                        this.taskForm.get('scheduled_to_time2').setValue(this.task.scheduled_to_time2 ? format(new Date(this.task.scheduled_to_time2), 'HH:mm:ss') : undefined);
                    } else {
                        this.taskForm.get('endTime2').disable();
                        this.taskForm.get('startTime2').disable();
                        this.taskForm.get('scheduled_from_time2').disable();
                        this.taskForm.get('scheduled_to_time2').disable();
                    }
                } else if (this.task.taskTypeTypology === TaskTypeTypology.withoutTime) {
                    this.taskForm.get('endTime2').disable();
                    this.taskForm.get('startTime2').disable();
                    this.taskForm.get('scheduled_from_time2').disable();
                    this.taskForm.get('scheduled_to_time2').disable();
                    if (this.task.scheduled_to_date) {
                        this.taskForm.get('scheduled_to_date').setValue(startOfDay(new Date(this.task.scheduled_to_date)));
                    }
                } else if (this.task.taskTypeTypology === TaskTypeTypology.taskAssigment) {
                    this.taskForm.get('address').disable();
                    this.taskForm.get('startDate').disable();
                    this.taskForm.get('endDate').disable();
                    this.taskForm.get('startTime').disable();
                    this.taskForm.get('endTime').disable();
                    this.taskForm.get('startTime2').disable();
                    this.taskForm.get('endTime2').disable();
                    this.taskForm.get('scheduled_from_time2').disable();
                    this.taskForm.get('scheduled_to_time2').disable();
                    this.taskForm.get('scheduled_to_date').setValue(startOfDay(new Date(this.task.scheduled_to_date)));
                }
                if (this.task.parentTaskId) { //La tarea se ha creado desde el móvil (sin fechas/horas de planificación)
                    this.taskForm.get('scheduled_from_date').disable();
                    this.taskForm.get('scheduled_to_date').disable();
                    this.taskForm.get('scheduled_from_time').disable();
                    this.taskForm.get('scheduled_from_time2').disable();
                    this.taskForm.get('scheduled_to_time').disable();
                    this.taskForm.get('scheduled_to_time2').disable();
                }
            });

        this.servicesService.service$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(service => this.service = service);

        combineLatest([
            this.tasksService.taskById$,
            this.servicesService.service$
        ]).pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async ([task, service]) => {
                this.userZombiesCalendar = await firstValueFrom(this.tasksService.getTaskAssignments(service.serviceId, task.task.planningAreaId, task.task.scheduled_from_date));
            });
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    discard() {
        this.discardBeautify.emit();
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    displayAddress(address): string {
        return address?.name ? address?.name : '';
    }

    beautifyTask() {
        this.disableButton = true;
        const task = this.taskForm.getRawValue();
        this.taskForm.markAllAsTouched();
        const valid = this.taskForm.valid;
        if (valid) {
            let body: FlushBeautifyTask;
            if (this.task.taskTypeTypology === TaskTypeTypology.taskAssigment) {
                body = {
                    taskId: task.taskId,
                    serviceId: this.service.serviceId,
                    externalTaskId: task.externalTaskId,
                    zombieNumber: task.zombie,
                    scheduled_from_date: format(typeof task.scheduled_from_date === 'string' ? parseISO(task.scheduled_from_date) : task.scheduled_from_date, 'yyyy-MM-dd'),
                    scheduled_to_date: format(typeof task.scheduled_to_date === 'string' ? parseISO(task.scheduled_to_date) : task.scheduled_to_date, 'yyyy-MM-dd'),
                    scheduled_from_time: task.scheduled_from_time,
                    scheduled_to_time: task.scheduled_to_time,
                };
            } else {
                body = {
                    taskId: task.taskId,
                    serviceId: this.service.serviceId,
                    externalTaskId: task.externalTaskId,
                    zombieNumber: task.zombie,
                    addressOid: task.address?._oid ?? this.task.addressOid,
                    scheduled_from_date: task.scheduled_from_date ? format(typeof task.scheduled_from_date === 'string' ? parseISO(task.scheduled_from_date) : task.scheduled_from_date, 'yyyy-MM-dd'): undefined,
                    scheduled_to_date: (this.task.taskTypeTypology === 'WITHOUT_TIME') && task.scheduled_to_date ?
                        format(typeof task.scheduled_to_date === 'string' ? parseISO(task.scheduled_to_date) : task.scheduled_to_date, 'yyyy-MM-dd') : undefined,
                    scheduled_from_time: task.scheduled_from_time,
                    scheduled_to_time: task.scheduled_to_time,
                    scheduled_from_time2: this.task.workDayTypeMode === 'SPLIT' ? task.scheduled_from_time2 : undefined,
                    scheduled_to_time2: this.task.workDayTypeMode === 'SPLIT' ? task.scheduled_to_time2 : undefined,
                    startTaskDate: format(typeof task.startDate === 'string' ? parseISO(task.startDate) : task.startDate, 'yyyy-MM-dd') + 'T'
                        + task.startTime,
                    startTaskDate2: this.task.workDayTypeMode === 'SPLIT' ? format(typeof task.startDate === 'string' ? parseISO(task.startDate) : task.startDate, 'yyyy-MM-dd')
                        + 'T' + task.startTime2 : undefined,
                    workDayTypeOid: task.workDayTypeOid,
                    workDayTypeUri: task.workDayTypeOid ? this.taskType.workDayTypeUri : undefined
                };
                if (this.task.taskTypeTypology === TaskTypeTypology.withTime) {
                    body.endTaskDate = format(typeof task.startDate === 'string' ? parseISO(task.startDate) : task.startDate, 'yyyy-MM-dd') + 'T' + task.endTime;
                    body.endTaskDate2 = this.task.workDayTypeMode === 'SPLIT' ? format(typeof task.startDate === 'string' ?
                        parseISO(task.startDate) : task.startDate, 'yyyy-MM-dd') + 'T' + task.endTime2 : undefined;
                } else {
                    body.endTaskDate = format(typeof task.endDate === 'string' ? parseISO(task.endDate) : task.endDate, 'yyyy-MM-dd') + 'T' + task.endTime;
                }
            }
            if (this.taskTypeOptions?.length > 0) {
                body.taskTypeId = task.taskTypeId;
            }
            this.tasksService.beautifyTask(body).subscribe({
                next: async (reply) => {
                    if (reply.task) {
                        this.showFlashMessage('success');
                        setTimeout(() => this.close(), 1000);
                    } else {
                        this.disableButton = false;
                        this.showFlashMessage('error');
                    }
                },
                error: (err) => {
                    this.disableButton = false;
                    console.error(err);
                    this.dialogsService.openErrorDialog();
                }
            });
        } else {
            this.dialogsService.openErrorDialog({
                message: this.transloco.translate('errorInvalidForm')
            });
            this.disableButton = false;
        }
    }

    private close() {
        this.closeBeautify.emit();
    }

    private showFlashMessage(type: 'success' | 'error'): void {
        // Show the message
        this.flashMessage = type;

        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Hide it after 3 seconds
        setTimeout(() => {

            this.flashMessage = null;

            // Mark for check
            this._changeDetectorRef.markForCheck();
        }, 3000);
    }
}
